/**
 * 托盘推荐算法
 * 按照客户-唛头维度分组，根据托盘容积和重量上限计算所需托盘数
 * 处理单件超限情况，优化托盘使用效率
 */

import { Mark, SortingDetail, Pallet, AlgorithmConfig, AlgorithmResult } from '../types';

/**
 * 生成唯一ID
 * @returns 唯一ID字符串
 */
function generateId(): string {
  return Math.random().toString(36).substr(2, 9);
}

/**
 * 生成托盘编号
 * @param index 托盘索引
 * @returns 托盘编号
 */
function generatePalletNo(index: number): string {
  return `PALLET-${String(index).padStart(4, '0')}`;
}

/**
 * 托盘推荐算法实现
 * @param marks 唛头列表
 * @param config 分拣配置
 * @returns 算法执行结果
 */
export function recommendPallets(marks: Mark[], config: AlgorithmConfig): AlgorithmResult {
  const pallets: Pallet[] = [];
  let palletIndex = 1;

  // 遍历每个唛头行，不再按客户-唛头维度分组
  marks.forEach(mark => {
    const { markPrefix, volumePerUnit, weightPerUnit, quantity } = mark;
    const customer = mark.customer ?? '';
    const sku = mark.sku ?? '';

    // 生成该唛头行的所有明细
    const details: SortingDetail[] = [];
    for (let i = 0; i < quantity; i++) {
      details.push({
        id: generateId(),
        customer,
        sku,
        markPrefix,
        markSerialNo: `${i + 1}`,
        volume: volumePerUnit,
        weight: weightPerUnit,
        status: 'pending'
      });
    }

    // 处理单件超限情况
    if (volumePerUnit > config.palletVolumeLimit || weightPerUnit > config.palletWeightLimit) {
      // 每件单独一个托盘
      details.forEach(detail => {
        const palletNo = generatePalletNo(palletIndex++);
        detail.palletNo = palletNo;

        pallets.push({
          palletNo,
          totalQuantity: 1,
          totalVolume: volumePerUnit,
          totalWeight: weightPerUnit,
          details: [detail],
          customer,
          sku
        });
      });
      return;
    }

    // 正常情况：计算所需托盘数并分配
    let currentPallet: Pallet | null = null;

    details.forEach(detail => {
      // 如果当前没有托盘或当前托盘已满，创建新托盘
      if (!currentPallet ||
          currentPallet.totalVolume + detail.volume > config.palletVolumeLimit ||
          currentPallet.totalWeight + detail.weight > config.palletWeightLimit) {

        const palletNo = generatePalletNo(palletIndex++);
        currentPallet = {
          palletNo,
          totalQuantity: 0,
          totalVolume: 0,
          totalWeight: 0,
          details: [],
          customer,
          sku
        };
        pallets.push(currentPallet);
      }

      // 将明细分配到当前托盘
      detail.palletNo = currentPallet.palletNo;
      currentPallet.details.push(detail);
      currentPallet.totalQuantity += 1;
      currentPallet.totalVolume += detail.volume;
      currentPallet.totalWeight += detail.weight;
    });
  });

  return {
    pallets,
    totalPallets: pallets.length
  };
}

/**
 * 计算托盘使用效率
 * @param pallets 托盘列表
 * @param config 分拣配置
 * @returns 托盘使用效率百分比
 */
export function calculatePalletEfficiency(pallets: Pallet[], config: AlgorithmConfig): number {
  if (pallets.length === 0) return 0;
  
  let totalVolumeUsage = 0;
  let totalWeightUsage = 0;
  const totalCapacity = pallets.length * (config.palletVolumeLimit + config.palletWeightLimit);
  
  pallets.forEach(pallet => {
    totalVolumeUsage += pallet.totalVolume;
    totalWeightUsage += pallet.totalWeight;
  });
  
  const totalUsage = totalVolumeUsage + totalWeightUsage;
  return Math.round((totalUsage / totalCapacity) * 100);
}
